<template>
  <!-- Minimalistic card for approved/rejected statuses -->
  <LiveRequestMinimalisticCard v-if="isMinimalisticCard" :request="request" @hide="handleHide" />

  <!-- Minimalistic card for resubmit/requested statuses -->
  <div
    v-else-if="isInteractiveCard"
    class="live-request-card interactive-card"
    :class="getStatusClass(request.status)"
  >
    <div class="interactive-content">
      <div class="campaign-info">
        <div class="campaign-id">
          {{ $t("dashboard.live_request_campaign_id", "Campaign ID") }}: {{ request.campaign_id }}
        </div>
        <div class="campaign-name">{{ campaignName }}</div>
        <div class="status-message">
          <svg
            v-if="request.status === 'resubmit'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            />
          </svg>
          <svg
            v-else-if="request.status === 'requested'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
            />
          </svg>
          {{ statusMessage }}
        </div>

        <!-- Requested status section -->
        <div v-if="request.status === 'requested'" class="requested-section">
          <!-- Last message -->
          <div v-if="lastMessage" class="last-message-compact">
            <div class="sender-badge-compact" :class="lastMessage.sender">
              <CdnLogoByLang v-if="lastMessage.sender === 'admin'" class="sender-logo-compact" />
              <svg v-else class="sender-logo-compact" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
              <span>{{
                lastMessage.sender === "admin"
                  ? $t("dashboard.live_request_sender_charidy", "Charidy")
                  : $t("dashboard.live_request_sender_you", "You")
              }}</span>
            </div>
            <div class="message-text-compact">{{ lastMessage.message }}</div>
          </div>

          <!-- Show previous messages button -->
          <button
            v-if="request.chat && request.chat.length > 1"
            class="show-previous-btn-compact"
            @click="togglePreviousMessages"
          >
            {{
              showPreviousMessages
                ? $t("dashboard.live_request_hide_previous", "Hide previous messages")
                : $t(
                    "dashboard.live_request_show_previous",
                    `See previous messages (${request.chat.length - 1})`
                  )
            }}
          </button>

          <!-- Previous messages -->
          <div v-if="showPreviousMessages" class="previous-messages-compact">
            <div v-for="(msg, index) in previousMessages" :key="index" class="last-message-compact">
              <div class="sender-badge-compact" :class="msg.sender">
                <CdnLogoByLang v-if="msg.sender === 'admin'" class="sender-logo-compact" />
                <svg v-else class="sender-logo-compact" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                <span>{{
                  msg.sender === "admin"
                    ? $t("dashboard.live_request_sender_charidy", "Charidy")
                    : $t("dashboard.live_request_sender_you", "You")
                }}</span>
              </div>
              <div class="message-text-compact">{{ msg.message }}</div>
            </div>
          </div>
        </div>

        <!-- Resubmit form inline -->
        <div v-if="request.status === 'resubmit'" class="resubmit-section">
          <!-- Last message -->
          <div v-if="lastMessage" class="last-message-compact">
            <div class="sender-badge-compact" :class="lastMessage.sender">
              <CdnLogoByLang v-if="lastMessage.sender === 'admin'" class="sender-logo-compact" />
              <svg v-else class="sender-logo-compact" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
              <span>{{
                lastMessage.sender === "admin"
                  ? $t("dashboard.live_request_sender_charidy", "Charidy")
                  : $t("dashboard.live_request_sender_you", "You")
              }}</span>
            </div>
            <div class="message-text-compact">{{ lastMessage.message }}</div>
          </div>

          <!-- Show previous messages button -->
          <button
            v-if="request.chat && request.chat.length > 1"
            class="show-previous-btn-compact"
            @click="togglePreviousMessages"
          >
            {{
              showPreviousMessages
                ? $t("dashboard.live_request_hide_previous", "Hide previous messages")
                : $t(
                    "dashboard.live_request_show_previous",
                    `See previous messages (${request.chat.length - 1})`
                  )
            }}
          </button>

          <!-- Previous messages -->
          <div v-if="showPreviousMessages" class="previous-messages-compact">
            <div v-for="(msg, index) in previousMessages" :key="index" class="last-message-compact">
              <div class="sender-badge-compact" :class="msg.sender">
                <CdnLogoByLang v-if="msg.sender === 'admin'" class="sender-logo-compact" />
                <svg v-else class="sender-logo-compact" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                <span>{{
                  msg.sender === "admin"
                    ? $t("dashboard.live_request_sender_charidy", "Charidy")
                    : $t("dashboard.live_request_sender_you", "You")
                }}</span>
              </div>
              <div class="message-text-compact">{{ msg.message }}</div>
            </div>
          </div>

          <!-- Payment warning -->
          <div
            v-if="!request.fee_data.campaign_paid || !request.fee_data.has_active_payment_option"
            class="campaign-warning"
          >
            <!-- Case 1: Payment not done, but payment option exists -->
            <span
              v-if="!request.fee_data.campaign_paid && request.fee_data.has_active_payment_option"
            >
              {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
              <a :href="request.fee_data.link_to_pay" class="campaign-link" target="_blank">
                {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
              </a>
              {{
                $t("dashboard.eligible_campaigns_payment_complete", "for your campaign to go live.")
              }}
            </span>

            <!-- Case 2: Payment not done AND no active payment option -->
            <span
              v-else-if="
                !request.fee_data.campaign_paid && !request.fee_data.has_active_payment_option
              "
            >
              {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
              <a :href="request.fee_data.link_to_pay" class="campaign-link" target="_blank">
                {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
              </a>
              {{
                $t(
                  "dashboard.eligible_campaigns_payment_middle",
                  "for your campaign and ensure you have an"
                )
              }}
              <router-link to="/gateways" class="campaign-link">
                {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
              </router-link>
              {{
                $t(
                  "dashboard.eligible_campaigns_warning_suffix",
                  "before submitting your live request."
                )
              }}
            </span>

            <!-- Case 3: Payment done but no active payment option -->
            <span
              v-else-if="
                request.fee_data.campaign_paid && !request.fee_data.has_active_payment_option
              "
            >
              {{ $t("dashboard.eligible_campaigns_ensure_payment", "Ensure you have an") }}
              <router-link to="/gateways" class="campaign-link">
                {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
              </router-link>
              {{
                $t(
                  "dashboard.eligible_campaigns_warning_suffix",
                  "before submitting your live request."
                )
              }}
            </span>
          </div>

          <!-- Error display -->
          <div v-if="submitError" class="error-message">
            {{ submitError }}
          </div>

          <!-- Input and submit -->
          <div v-if="request.fee_data.passed_validation" class="resubmit-inline-form">
            <input
              v-model="resubmitMessage"
              type="text"
              class="resubmit-input"
              :placeholder="
                $t('dashboard.live_request_resubmit_placeholder', 'Type your response here...')
              "
              @keyup.enter="handleResubmit"
            />
            <button
              class="resubmit-btn"
              @click.prevent="handleResubmit"
              :disabled="resubmitLoading || !resubmitMessage.trim()"
            >
              {{
                resubmitLoading
                  ? $t("dashboard.loading", "Loading...")
                  : $t("dashboard.live_request_resubmit_btn", "Submit")
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Full card for other statuses -->
  <div v-else class="live-request-card" :class="[cardClass, getStatusClass(request.status)]">
    <div class="card-header">
      <h3 v-if="isMainRequest" class="card-title">
        {{ $t("dashboard.live_request_title", "Request campaign to go live") }}
      </h3>
      <h4 v-else class="card-title">
        {{ $t("dashboard.live_request_title", "Request campaign to go live") }} #{{ request.id }}
      </h4>
      <div class="header-actions">
        <span class="status-badge" :class="getStatusClass(request.status)">
          {{ formatStatus(request.status) }}
        </span>
        <button
          v-if="shouldShowHideButton"
          class="hide-btn"
          @click="handleHide"
          :title="$t('dashboard.live_request_hide', 'Hide this request')"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
          </svg>
        </button>
      </div>
    </div>

    <div class="card-content">
      <div class="request-info">
        <div class="info-row">
          <span class="label">{{ $t("dashboard.live_request_campaign_id", "Campaign ID") }}:</span>
          <span class="value">{{ request.campaign_id }}</span>
        </div>

        <div class="info-row">
          <span class="label">{{ $t("dashboard.live_request_created_at", "Created") }}:</span>
          <span class="value">{{ formatDate(request.created_at) }}</span>
        </div>
        <div class="info-row" v-if="request.chat && request.chat.length > 0">
          <span class="label">{{ $t("dashboard.live_request_message", "Messages") }}:</span>
          <div class="chat-messages">
            <div class="chat-message" :key="request.chat.length - 1">
              <div class="sender-badge" :class="lastMessage.sender">
                <CdnLogoByLang v-if="lastMessage.sender === 'admin'" class="sender-logo" />
                <img
                  v-else-if="lastMessage.sender === 'organization' && organizationLogo"
                  :src="organizationLogo"
                  :alt="$t('dashboard.live_request_organization_logo', 'Organization')"
                  class="sender-logo"
                />
                <svg
                  v-else-if="lastMessage.sender === 'organization' && !organizationLogo"
                  class="sender-logo sender-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                <span>{{
                  lastMessage.sender === "admin"
                    ? $t("dashboard.live_request_sender_charidy", "Charidy")
                    : $t("dashboard.live_request_sender_you", "You")
                }}</span>
              </div>
              <span class="message-text">{{ lastMessage.message }}</span>
            </div>
            <button
              v-if="request.chat.length > 1"
              class="show-previous-btn"
              @click="togglePreviousMessages"
            >
              {{
                showPreviousMessages
                  ? $t("dashboard.live_request_hide_previous", "Hide previous messages")
                  : $t(
                      "dashboard.live_request_show_previous",
                      `See previous messages (${request.chat.length - 1})`
                    )
              }}
            </button>
            <div v-if="showPreviousMessages" class="previous-messages">
              <div v-for="(msg, index) in previousMessages" :key="index" class="chat-message">
                <div class="sender-badge" :class="msg.sender">
                  <CdnLogoByLang v-if="msg.sender === 'admin'" class="sender-logo" />
                  <img
                    v-else-if="msg.sender === 'organization' && organizationLogo"
                    :src="organizationLogo"
                    :alt="$t('dashboard.live_request_organization_logo', 'Organization')"
                    class="sender-logo"
                  />
                  <svg
                    v-else-if="msg.sender === 'organization' && !organizationLogo"
                    class="sender-logo sender-icon"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                    />
                  </svg>
                  <span>{{
                    msg.sender === "admin"
                      ? $t("dashboard.live_request_sender_charidy", "Charidy")
                      : $t("dashboard.live_request_sender_you", "You")
                  }}</span>
                </div>
                <span class="message-text">{{ msg.message }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment warning for resubmit -->
        <div
          v-if="
            request.status === 'resubmit' && request.fee_data && !request.fee_data.passed_validation
          "
          class="campaign-warning"
        >
          <!-- Case 1: Payment not done, but payment option exists -->
          <span
            v-if="!request.fee_data.campaign_paid && request.fee_data.has_active_payment_option"
          >
            {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
            <a :href="request.fee_data.link_to_pay" class="campaign-link" target="_blank">
              {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
            </a>
            {{
              $t("dashboard.eligible_campaigns_payment_complete", "for your campaign to go live.")
            }}
          </span>

          <!-- Case 2: Payment not done AND no active payment option -->
          <span
            v-else-if="
              !request.fee_data.campaign_paid && !request.fee_data.has_active_payment_option
            "
          >
            {{ $t("dashboard.eligible_campaigns_payment_warning", "Please") }}
            <a :href="request.fee_data.link_to_pay" class="campaign-link" target="_blank">
              {{ $t("dashboard.eligible_campaigns_pay_here", "pay here") }}
            </a>
            {{
              $t(
                "dashboard.eligible_campaigns_payment_middle",
                "for your campaign and ensure you have an"
              )
            }}
            <router-link to="/gateways" class="campaign-link">
              {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
            </router-link>
            {{
              $t(
                "dashboard.eligible_campaigns_warning_suffix",
                "before submitting your live request."
              )
            }}
          </span>

          <!-- Case 3: Payment done but no active payment option -->
          <span
            v-else-if="
              request.fee_data.campaign_paid && !request.fee_data.has_active_payment_option
            "
          >
            {{ $t("dashboard.eligible_campaigns_ensure_payment", "Ensure you have an") }}
            <router-link to="/gateways" class="campaign-link">
              {{ $t("dashboard.eligible_campaigns_warning_payment", "active payment option") }}
            </router-link>
            {{
              $t(
                "dashboard.eligible_campaigns_warning_suffix",
                "before submitting your live request."
              )
            }}
          </span>
        </div>

        <div class="resubmit-form" v-if="request.status === 'resubmit'">
          <div class="form-header">
            <!-- <svg class="attention-icon" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              />
            </svg> -->
            <!-- <div class="header-text">
              <h4>{{ $t("dashboard.live_request_action_required", "Action Required") }}</h4>
              <p class="helper-text">
                {{
                  $t(
                    "dashboard.live_request_resubmit_helper",
                    "Please provide a response to the message above before resubmitting your request."
                  )
                }}
              </p>
            </div> -->
          </div>
          <!-- Error display -->
          <div v-if="submitError" class="error-message">
            {{ submitError }}
          </div>

          <!-- <div v-if="request.fee_data.passed_validation">
            <div class="form-group">
              <label
                >{{ $t("dashboard.live_request_resubmit_message", "Your Response") }}
                <span class="required">*</span></label
              >
              <textarea
                v-model="resubmitMessage"
                class="form-control"
                rows="4"
                :placeholder="
                  $t('dashboard.live_request_resubmit_placeholder', 'Type your response here...')
                "
                required
              ></textarea>
            </div>
            <button
              class="bttn bttn--sm bttn--orange"
              @click.prevent="handleResubmit"
              :disabled="resubmitLoading || !resubmitMessage.trim()"
            >
              {{
                resubmitLoading
                  ? $t("dashboard.loading", "Loading...")
                  : $t("dashboard.live_request_resubmit_btn", "Resubmit")
              }}
            </button>
          </div>

          <div v-else class="validation-warning">
            {{
              $t(
                "dashboard.live_request_validation_required",
                "Campaign validation is required before submitting a live request."
              )
            }}
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import CdnLogoByLang from "@/common-components/cdn-v2/cdn-logo-by-lang.vue";
import LiveRequestMinimalisticCard from "./live-request-minimalistic-card.vue";

export default defineComponent({
  name: "LiveRequestItem",

  components: {
    CdnLogoByLang,
    LiveRequestMinimalisticCard,
  },

  props: {
    request: {
      type: Object,
      required: true,
    },
    isMainRequest: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      resubmitMessage: "",
      resubmitLoading: false,
      showPreviousMessages: false,
      submitError: null,
    };
  },

  computed: {
    ...mapGetters({
      getCurrentOrganization: "getCurrentOrganization",
    }),

    isMinimalisticCard() {
      return this.request.status === "approved" || this.request.status === "rejected";
    },

    isInteractiveCard() {
      return this.request.status === "resubmit" || this.request.status === "requested";
    },

    shouldShowHideButton() {
      // Show hide button only for approved or rejected statuses
      return this.request.status === "approved" || this.request.status === "rejected";
    },

    campaignName() {
      return this.request.campaign?.campaigntitle || "";
    },

    statusMessage() {
      if (this.request.status === "approved") {
        return this.$t("dashboard.live_request_approved_message", "Campaign successfully approved");
      } else if (this.request.status === "rejected") {
        return this.$t("dashboard.live_request_rejected_message", "Campaign rejected");
      } else if (this.request.status === "resubmit") {
        return this.$t(
          "dashboard.live_request_resubmit_message",
          "Action required - Please resubmit"
        );
      } else if (this.request.status === "requested") {
        return this.$t("dashboard.live_request_requested_message", "Request pending review");
      }
      return "";
    },

    cardClass() {
      return this.isMainRequest ? "main-request" : "additional-request";
    },

    organizationLogo() {
      return this.getCurrentOrganization?.attributes?.logo || null;
    },

    lastMessage() {
      return this.request.chat?.[this.request.chat.length - 1] || null;
    },

    previousMessages() {
      const messages = this.request.chat?.slice(0, -1) || [];
      return messages.reverse();
    },
  },

  methods: {
    handleHide() {
      this.$emit("hide", this.request.id);
    },

    togglePreviousMessages() {
      this.showPreviousMessages = !this.showPreviousMessages;
    },

    async handleResubmit() {
      if (this.resubmitLoading) return;

      this.resubmitLoading = true;
      this.submitError = null; // Clear previous errors

      try {
        const success = await this.$store.dispatch("resubmitLiveRequest", {
          campaignId: this.request.campaign_id,
          requestId: this.request.id,
          message: this.resubmitMessage,
        });

        if (success) {
          this.resubmitMessage = "";
          this.submitError = null;
          this.$emit("resubmitted");
        }
      } catch (error) {
        this.submitError = this.parseErrorMessage(error);
      } finally {
        this.resubmitLoading = false;
      }
    },

    parseErrorMessage(error) {
      try {
        // Handle structured error response like the one provided
        if (error && error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
          // Return the first error message from the errors array
          return (
            error.errors[0].message ||
            this.$t("dashboard.generic_error", "An error occurred. Please try again.")
          );
        }

        // Handle simple error message
        if (error && error.message) {
          return error.message;
        }

        // Handle string error
        if (typeof error === "string") {
          return error;
        }

        // Fallback to generic error
        return this.$t("dashboard.generic_error", "An error occurred. Please try again.");
      } catch (parseError) {
        console.error("Error parsing error message:", parseError);
        return this.$t("dashboard.generic_error", "An error occurred. Please try again.");
      }
    },

    formatStatus(status) {
      const statusMap = {
        request: this.$t("dashboard.live_request_status_request", "Request"),
        pending: this.$t("dashboard.live_request_status_pending", "Pending"),
        approved: this.$t("dashboard.live_request_status_approved", "Approved"),
        rejected: this.$t("dashboard.live_request_status_rejected", "Rejected"),
        resubmit: this.$t("dashboard.live_request_status_resubmit", "Resubmit"),
      };
      return statusMap[status] || status;
    },

    getStatusClass(status) {
      return {
        "status-request": status === "request" || status === "pending" || status === "requested",
        "status-pending": status === "pending",
        "status-approved": status === "approved",
        "status-rejected": status === "rejected",
        "status-resubmit": status === "resubmit",
      };
    },

    formatDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      } catch (error) {
        return dateString;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.live-request-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border-left: 4px solid #6c757d; // Default color

  &.main-request {
    border-left-color: #107598;
  }

  &.status-pending {
    border-left-color: #ffc107;
  }

  &.status-request {
    border-left-color: #17a2b8;
  }

  &.status-resubmit {
    border-left-color: #fd7e14;
  }

  &.status-approved {
    border-left-color: #28a745;
  }

  &.status-rejected {
    border-left-color: #dc3545;
  }

  &.additional-request {
    margin-bottom: 12px;
  }

  // Interactive card styling for resubmit/requested statuses
  &.interactive-card {
    border: none;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    margin-bottom: 12px;

    .interactive-content {
      padding: 16px 20px;

      .campaign-info {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .campaign-id {
          font-size: 13px;
          font-weight: 600;
          color: #495057;
        }

        .campaign-name {
          font-size: 16px;
          font-weight: 700;
          color: #212529;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;

          .status-icon {
            width: 18px;
            height: 18px;
          }
        }
      }
    }

    &.status-resubmit {
      background: linear-gradient(135deg, #fdf6e3 0%, #f9e9c0 100%);
      border-left: 4px solid #d4a340;

      .status-message {
        color: #7a5e1a;
      }

      .resubmit-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 12px;

        .last-message-compact {
          background: rgba(255, 255, 255, 0.7);
          padding: 10px 12px;
          border-radius: 6px;
          border-left: 3px solid #d4a340;

          .sender-badge-compact {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;

            .sender-logo-compact {
              width: 16px;
              height: 16px;
              object-fit: cover;
              border-radius: 50%;
            }

            &.admin {
              color: #107598;
            }

            &.organization {
              color: #2196f3;
            }
          }

          .message-text-compact {
            font-size: 13px;
            color: #495057;
            line-height: 1.5;
            word-wrap: break-word;
            font-size: 18px;
            font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
          }
        }

        .show-previous-btn-compact {
          align-self: flex-start;
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid #d4a340;
          border-radius: 4px;
          color: #7a5e1a;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(255, 255, 255, 1);
            border-color: #b8892f;
          }

          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(212, 163, 64, 0.25);
          }
        }

        .previous-messages-compact {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .resubmit-inline-form {
          display: flex;
          gap: 8px;
          margin-top: 4px;

          .resubmit-input {
            flex: 1;
            padding: 10px 14px;
            border: 2px solid #d4a340;
            border-radius: 6px;
            font-size: 14px;
            background: #ffffff;
            transition: all 0.2s ease;

            &::placeholder {
              color: #adb5bd;
            }

            &:focus {
              outline: none;
              border-color: #b8892f;
              box-shadow: 0 0 0 3px rgba(212, 163, 64, 0.15);
            }
          }

          .resubmit-btn {
            padding: 10px 20px;
            background: #d4a340;
            color: #ffffff;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;

            &:hover:not(:disabled) {
              background: #b8892f;
              transform: translateY(-1px);
              box-shadow: 0 2px 6px rgba(212, 163, 64, 0.3);
            }

            &:active:not(:disabled) {
              transform: translateY(0);
            }

            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }

            &:focus {
              outline: none;
              box-shadow: 0 0 0 3px rgba(212, 163, 64, 0.25);
            }
          }
        }
      }
    }

    &.status-request {
      background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
      border-left: 4px solid #2196f3;

      .status-message {
        color: #0d47a1;
      }

      .requested-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 12px;

        .last-message-compact {
          background: rgba(255, 255, 255, 0.7);
          padding: 10px 12px;
          border-radius: 6px;
          border-left: 3px solid #2196f3;

          .sender-badge-compact {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;

            .sender-logo-compact {
              width: 16px;
              height: 16px;
              object-fit: cover;
              border-radius: 50%;
            }

            &.admin {
              color: #107598;
            }

            &.organization {
              color: #2196f3;
            }
          }

          .message-text-compact {
            font-size: 13px;
            color: #495057;
            line-height: 1.5;
            word-wrap: break-word;
          }
        }

        .show-previous-btn-compact {
          align-self: flex-start;
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid #2196f3;
          border-radius: 4px;
          color: #0d47a1;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(255, 255, 255, 1);
            border-color: #1976d2;
          }

          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.25);
          }
        }

        .previous-messages-compact {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      }
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;

  .card-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333333;

    .additional-request & {
      font-size: 16px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .hide-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    background: transparent;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    cursor: pointer;
    color: #6c757d;
    transition: all 0.2s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background-color: #f8d7da;
      border-color: #dc3545;
      color: #dc3545;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
    }
  }
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-pending {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }

  &.status-request {
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }

  &.status-resubmit {
    background-color: #ffe5d0;
    color: #fd7e14;
    border: 1px solid #ffc9a7;
  }

  &.status-approved {
    background-color: #c8e6c9;
    color: #1b5e20;
    border: 1px solid #a5d6a7;
  }

  &.status-rejected {
    background-color: #ffcdd2;
    color: #b71c1c;
    border: 1px solid #ef9a9a;
  }
}

.card-content {
  padding: 20px;
}

.request-info {
  .info-row {
    display: flex;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      font-weight: 600;
      color: #6c757d;
      min-width: 120px;
      margin-right: 12px;
    }

    .value {
      color: #333333;
      word-break: break-word;
    }
  }
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
}

.show-previous-btn {
  align-self: flex-start;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  color: #107598;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #107598;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(16, 117, 152, 0.25);
  }
}

.previous-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 4px;
}

.chat-message {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    border-color: #d0d0d0;
  }

  .sender-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    width: fit-content;

    .sender-logo {
      width: 20px;
      height: 20px;
      object-fit: cover;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .sender-icon {
      background-color: rgba(255, 255, 255, 0.3);
      padding: 2px;
    }

    &.admin {
      background-color: #107598;
      color: #ffffff;
      box-shadow: 0 2px 4px rgba(16, 117, 152, 0.2);
    }

    &.organization {
      background-color: #2196f3;
      color: #ffffff;
      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
    }
  }

  .message-text {
    color: #2c3e50;
    white-space: pre-wrap;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 600;
    padding: 8px 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial,
      sans-serif;
  }
}

.resubmit-form {
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #2196f3;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);

  .form-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 10px;
      font-weight: 700;
      font-size: 15px;
      color: #495057;

      .required {
        color: #dc3545;
        margin-left: 2px;
        font-size: 18px;
      }
    }

    .form-control {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid #2196f3;
      border-radius: 8px;
      font-size: 15px;
      background-color: #ffffff;
      transition: all 0.3s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

      &::placeholder {
        color: #adb5bd;
        font-style: italic;
      }

      // &:focus {
      //   border-color: #1976d2;
      //   outline: none;
      //   box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.25), 0 4px 8px rgba(0, 0, 0, 0.12);
      //   background-color: #f5faff;
      // }

      &:hover {
        border-color: #1976d2;
      }
    }
  }
}

.campaign-warning {
  background: #fff3cd;
  color: #856404;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid #ffeeba;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  .campaign-link {
    color: #856404;
    text-decoration: underline;
    margin: 0 4px;

    &:hover {
      color: #6c5a00;
      text-decoration: none;
    }
  }
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  border: 1px solid #f5c6cb;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.validation-warning {
  background: #fff3cd;
  color: #856404;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  border: 1px solid #ffeeba;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@media (max-width: 768px) {
  .card-content {
    padding: 16px;
  }

  .card-header {
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .card-title {
      font-size: 16px;
    }
  }

  .request-info {
    .info-row {
      flex-direction: column;
      gap: 4px;

      .label {
        min-width: auto;
        margin-right: 0;
      }
    }
  }
}
</style>
